import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import type {
  HostedFlowConfig,
  PayloadResultScenarios,
} from "@portal/app/_server/services/formFunnel/payloadTypes";
import { useClickoutRedirect } from "./ClickoutResult";
import { HostedResult, HostedResultProps } from "./HostedResult";
import { ProgramResultCard, type ProgramResultOption } from "./ProgramResultCard";
import type { FunnelProgressUpdate } from "../utils/funnelProgress";
import { useFormFunnelTracking } from "../hooks/useFormFunnelTracking";
import { useFormFunnelHeadingLevel } from "../FormFunnelHeadingContext";

export type MultiResultOption = ProgramResultOption;

export interface MultiResultScenario {
  type: "MULTI_RESULT";
  results: MultiResultOption[];
}

interface MultiResultChooserProps {
  scenario: MultiResultScenario;
  clickoutConfig?: PayloadResultScenarios["clickoutConfig"];
  sessionId?: string;
  onHostedComplete?: (
    redirectUrl?: string,
    firstName?: string,
    context?: {
      program: HostedResultProps["program"];
      hostedFlowConfig?: HostedFlowConfig;
    }
  ) => void;
  isPreview?: boolean;
  funnelId?: string;
  formFunnelSessionId?: string;
  answers?: Record<string, unknown>;
  onProgressChange?: (progress: FunnelProgressUpdate) => void;
}

/**
 * MUI cuts the rule with ::before/::after rather than masking it behind an
 * opaque label, so this stays correct on any surface — the funnel background is
 * not always white, and a hand-rolled version would show as a white block.
 */
const AlsoWorthALookDivider = () => (
  <Divider
    data-testid="multi-result-divider"
    sx={{
      "&::before, &::after": { borderColor: (theme) => theme.palette.dream.borderColor },
      "& .MuiDivider-wrapper": {
        fontSize: "0.85rem",
        fontWeight: 600,
        color: (theme) => theme.palette.dream.grayMediumDark,
      },
    }}
  >
    Also worth a look
  </Divider>
);

const ClickoutResultCard = ({
  option,
  sessionId,
  isSubmitting,
  onSubmitStart,
  isPreview,
  funnelId,
  formFunnelSessionId,
  answers,
  clickoutCtaText,
  programHeadingComponent,
}: {
  option: Extract<MultiResultOption, { type: "CLICKOUT" }>;
  sessionId?: string;
  isSubmitting?: boolean;
  onSubmitStart: () => void;
  isPreview?: boolean;
  funnelId?: string;
  formFunnelSessionId?: string;
  answers?: Record<string, unknown>;
  clickoutCtaText?: string;
  programHeadingComponent: "h2" | "h3";
}) => {
  const { handleRedirect, isRedirecting } = useClickoutRedirect({
    program: option.program,
    sessionId,
    isPreview,
    funnelId,
    formFunnelSessionId,
    answers,
    prePingResult: option.prePingResult,
    selectedResultRank: option.rank,
  });

  return (
    <ProgramResultCard
      option={option}
      isSubmitting={isSubmitting || isRedirecting}
      isPreview={isPreview}
      clickoutCtaText={clickoutCtaText}
      programHeadingComponent={programHeadingComponent}
      onSelect={() => {
        if (isPreview || isSubmitting || isRedirecting) return;
        onSubmitStart();
        void handleRedirect();
      }}
    />
  );
};

export const MultiResultChooser = ({
  scenario,
  clickoutConfig,
  sessionId,
  onHostedComplete,
  isPreview = false,
  funnelId,
  formFunnelSessionId,
  answers,
  onProgressChange,
}: MultiResultChooserProps) => {
  const headingLevel = useFormFunnelHeadingLevel();
  const programHeadingLevel = headingLevel === "h2" ? "h3" : "h2";
  const { trackHostedFlowStep } = useFormFunnelTracking();
  const [selectedHostedOption, setSelectedHostedOption] = useState<Extract<
    MultiResultOption,
    { type: "HOSTED" }
  > | null>(null);
  const isHostedActiveRef = useRef(false);
  const [isClickoutSubmitting, setIsClickoutSubmitting] = useState(false);
  // True while ConfirmStep's submitFormFunnelLead is in flight. The mutation
  // survives unmount, so the first-step exit must stay locked until it settles;
  // otherwise the user could submit a second card's lead concurrently (and the
  // first completion would yank the second flow to its success page).
  const [isHostedSubmitting, setIsHostedSubmitting] = useState(false);

  const reportHostedProgress = useCallback(
    (progress: FunnelProgressUpdate) => {
      // Back can run before React flushes a previously committed hosted effect.
      // Ignore that pending report before it restores the old progress floor.
      if (isHostedActiveRef.current) onProgressChange?.(progress);
    },
    [onProgressChange]
  );

  useEffect(() => {
    if (!selectedHostedOption) {
      onProgressChange?.({ phase: "results" });
    }
  }, [selectedHostedOption, onProgressChange]);

  if (selectedHostedOption) {
    return (
      <Box data-testid="multi-result-hosted-flow">
        <HostedResult
          program={selectedHostedOption.program}
          hostedFlowConfig={selectedHostedOption.hostedFlowConfig}
          funnelQuestions={selectedHostedOption.funnelQuestions}
          staticProgramHighlights={selectedHostedOption.staticProgramHighlights}
          onComplete={(redirectUrl, firstName) =>
            onHostedComplete?.(redirectUrl, firstName, {
              program: selectedHostedOption.program,
              hostedFlowConfig: selectedHostedOption.hostedFlowConfig,
            })
          }
          isPreview={isPreview}
          funnelId={funnelId}
          formFunnelSessionId={formFunnelSessionId}
          answers={answers}
          searchUrl={selectedHostedOption.searchUrl}
          onProgressChange={reportHostedProgress}
          onBackFromFirstStep={() => {
            if (isHostedSubmitting) return;
            isHostedActiveRef.current = false;
            onProgressChange?.({ phase: "results", direction: "back" });
            setSelectedHostedOption(null);
          }}
          prePingResult={selectedHostedOption.prePingResult}
          selectedResultRank={selectedHostedOption.rank}
          onSubmittingChange={setIsHostedSubmitting}
        />
      </Box>
    );
  }

  return (
    <Box
      data-testid="multi-result-chooser"
      sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}
    >
      <Typography variant="h5" component={headingLevel} sx={{ fontWeight: 800, lineHeight: 1.2 }}>
        Your top programs
      </Typography>

      {scenario.results.map((option, index) => (
        <Fragment key={`${option.type}-${option.program.id}-${option.rank}`}>
          {/*
            Keyed off the previous card's rank, not its index, so it stays tied
            to the same predicate as the best-match styling. mapResponseToScenario
            filters unmappable matches without re-sorting, so a dropped rank 1
            would otherwise put the divider under a card that is not the top one.
          */}
          {index > 0 && scenario.results[index - 1].rank === 1 && <AlsoWorthALookDivider />}
          {option.type === "CLICKOUT" ? (
            <ClickoutResultCard
              option={option}
              sessionId={sessionId}
              isSubmitting={isClickoutSubmitting}
              onSubmitStart={() => setIsClickoutSubmitting(true)}
              isPreview={isPreview}
              funnelId={funnelId}
              formFunnelSessionId={formFunnelSessionId}
              answers={answers}
              clickoutCtaText={clickoutConfig?.ctaText}
              programHeadingComponent={programHeadingLevel}
            />
          ) : (
            <ProgramResultCard
              option={option}
              isSubmitting={isClickoutSubmitting}
              isPreview={isPreview}
              programHeadingComponent={programHeadingLevel}
              onSelect={() => {
                if (isClickoutSubmitting || isHostedActiveRef.current) return;
                isHostedActiveRef.current = true;
                if (!isPreview) trackHostedFlowStep("school_match");
                setSelectedHostedOption(option);
              }}
            />
          )}
        </Fragment>
      ))}
    </Box>
  );
};
